/**
 * Media handling utilities for FarmDetailsModal PDF generation.
 * Extracted from FarmDetailsModal.tsx.
 */

/**
 * Convert image URL to JPEG base64 data URL.
 * Uses fetch for CORS robustness.
 */
export async function convertImageToJpegBase64(
  imageUrl: string,
): Promise<string> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    return await new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (!ctx) return reject(new Error("Could not get canvas context"));
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
        URL.revokeObjectURL(objectUrl);
        resolve(dataUrl);
      };
      img.onerror = (err) => {
        URL.revokeObjectURL(objectUrl);
        console.error("Error processing image blob:", imageUrl);
        reject(err);
      };
      img.src = objectUrl;
    });
  } catch (error) {
    console.error("Failed to fetch or convert image:", imageUrl, error);
    throw error;
  }
}

/**
 * Convert image URL to data URL, returning null on error.
 */
export async function convertImageToDataUrl(
  imageUrl: string,
): Promise<string | null> {
  try {
    return await convertImageToJpegBase64(imageUrl);
  } catch (error) {
    console.error("Failed to convert image to data URL:", imageUrl, error);
    return null;
  }
}

/**
 * Generate a thumbnail from video URL.
 * Returns base64 data URL of the middle frame.
 */
export async function generateVideoThumbnail(
  videoUrl: string,
): Promise<string | null> {
  try {
    const response = await fetch(videoUrl);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);

    return await new Promise((resolve) => {
      const video = document.createElement("video");
      video.preload = "metadata";

      const cleanup = () => {
        video.removeEventListener("seeked", onSeeked);
        video.removeEventListener("loadedmetadata", onLoadedMetadata);
        video.removeEventListener("error", onError);
        URL.revokeObjectURL(objectUrl);
        video.remove();
      };

      const onSeeked = () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d");
          if (!ctx) return resolve(null);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
          cleanup();
          resolve(dataUrl);
        } catch (e) {
          console.error("Error generating thumbnail from canvas", e);
          cleanup();
          resolve(null);
        }
      };

      const onLoadedMetadata = () => {
        video.currentTime = Math.min(1, video.duration / 2);
      };

      const onError = () => {
        console.error("Error loading video blob for thumbnail:", videoUrl);
        cleanup();
        resolve(null);
      };

      video.addEventListener("loadedmetadata", onLoadedMetadata);
      video.addEventListener("seeked", onSeeked);
      video.addEventListener("error", onError);
      video.src = objectUrl;
    });
  } catch (error) {
    console.error("Failed to fetch video for thumbnail:", videoUrl, error);
    return null;
  }
}

/**
 * Fetch header logo and convert to base64 data URL.
 * Supports dynamic logos with fallback to static logo.
 */
export async function fetchHeaderLogo(
  dynamicHeaderLogoUrl: string | null,
): Promise<string | null> {
  let headerLogoDataUrl: string | null = null;

  if (dynamicHeaderLogoUrl) {
    try {
      if (
        !dynamicHeaderLogoUrl.startsWith("http://") &&
        !dynamicHeaderLogoUrl.startsWith("https://") &&
        !dynamicHeaderLogoUrl.startsWith("data:")
      ) {
        console.warn("Invalid header logo URL format:", dynamicHeaderLogoUrl);
      } else {
        const response = await fetch(dynamicHeaderLogoUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        headerLogoDataUrl = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onerror = reject;
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      }
    } catch (e) {
      console.error("Failed to fetch dynamic header logo:", e);
      try {
        const response = await fetch("/maestros_logo.png");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const blob = await response.blob();
        headerLogoDataUrl = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(blob);
        });
      } catch (fallbackError) {
        console.error("Failed to fetch fallback header logo:", fallbackError);
      }
    }
  } else {
    try {
      const response = await fetch("/maestros_logo.png");
      const blob = await response.blob();
      headerLogoDataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.error("Failed to fetch fallback header logo:", e);
    }
  }

  return headerLogoDataUrl;
}
